(() => {
  'use strict';

  // Dawn's AJAX product-form component is deliberately not used: the native
  // Shopify product form keeps the standard /cart/add submission and errors.
  const initializeProduct = (root) => {
    if (root.dataset.tdmInitialized === 'true') return;
    const dataElement = root.querySelector('[data-tdm-product-data]');
    const variantControl = root.querySelector('[data-tdm-variant]');
    const form = root.querySelector('.tdm-product-form');
    if (!dataElement) return;

    let data;
    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(data.variants) || !data.variants.length) return;
    root.dataset.tdmInitialized = 'true';

    const price = root.querySelector('[data-tdm-price]');
    const comparePrice = root.querySelector('[data-tdm-compare]');
    const submit = root.querySelector('[data-tdm-submit]');
    const availability = root.querySelector('[data-tdm-availability]');
    const quantity = root.querySelector('[data-tdm-quantity]');
    const media = Array.from(root.querySelectorAll('[data-tdm-media]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-tdm-thumbnail]'));
    const variants = new Map(data.variants.map((variant) => [String(variant.id), variant]));
    const defaultVariant = data.variants.find((variant) => variant.available) || data.variants[0];
    const money = new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: data.currency || 'MXN',
      currencyDisplay: 'code',
    });

    const activateMedia = (id) => {
      const target = media.find((item) => item.dataset.tdmMedia === String(id));
      if (!target) return;
      media.forEach((item) => { item.hidden = item !== target; });
      thumbnails.forEach((link) => {
        if (link.dataset.tdmThumbnail === String(id)) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    const syncQuantity = (variant) => {
      if (!quantity) return;
      const minimum = Math.max(1, Number(variant.quantityMin) || 1);
      const increment = Math.max(1, Number(variant.quantityIncrement) || 1);
      const maximum = variant.quantityMax == null ? null : Number(variant.quantityMax);
      quantity.min = String(minimum);
      quantity.step = String(increment);
      if (maximum == null) quantity.removeAttribute('max');
      else quantity.max = String(maximum);

      let value = Math.max(minimum, Number(quantity.value) || minimum);
      value = minimum + Math.ceil((value - minimum) / increment) * increment;
      if (maximum != null && value > maximum) {
        value = minimum + Math.floor((maximum - minimum) / increment) * increment;
      }
      quantity.value = String(Math.max(minimum, value));
    };

    const renderVariant = (variant, updateHistory = false) => {
      if (!variant) return;
      if (variantControl) variantControl.value = String(variant.id);
      if (price) price.textContent = money.format(Number(variant.price) / 100);
      if (comparePrice) {
        const isSale = Number(variant.compareAtPrice) > Number(variant.price);
        comparePrice.hidden = !isSale;
        comparePrice.textContent = isSale ? money.format(Number(variant.compareAtPrice) / 100) : '';
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? (submit.dataset.tdmSubmitLabel || 'Agregar al carrito') : 'Agotado';
      }
      if (availability) {
        availability.textContent = variant.available ? 'Disponible' : 'Esta opción está agotada.';
      }
      syncQuantity(variant);
      if (variant.mediaId) activateMedia(variant.mediaId);

      if (updateHistory) {
        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') !== String(variant.id)) {
          url.searchParams.set('variant', String(variant.id));
          url.searchParams.delete('sesiones');
          window.history.pushState(window.history.state, '', url);
        }
      }
    };

    const variantFromUrl = () => {
      const params = new URL(window.location.href).searchParams;
      const id = params.get('variant');
      if (id && variants.has(id)) return variants.get(id);
      const sessions = params.get('sesiones');
      if (!data.isConsultation || !['1', '3', '5'].includes(sessions)) return undefined;
      return data.variants.find((variant) => {
        const title = String(variant.title || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return new RegExp(`(?:^|\\D)${sessions}(?:\\s*sesion(?:es)?\\b|$)`).test(title);
      });
    };

    if (media.length) activateMedia(media[0].dataset.tdmMedia);
    renderVariant(variantFromUrl() || variants.get(variantControl?.value) || defaultVariant);

    variantControl?.addEventListener('change', () => {
      renderVariant(variants.get(variantControl.value), true);
    });

    thumbnails.forEach((link) => {
      link.addEventListener('click', (event) => {
        // Preserve the link's ordinary open-in-new-tab behavior.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        activateMedia(link.dataset.tdmThumbnail);
      });
    });

    form?.addEventListener('submit', (event) => {
      const variant = variants.get(variantControl.value);
      if (!variant || !variant.available) {
        event.preventDefault();
        if (availability) availability.textContent = 'Esta opción está agotada. Elige otra opción disponible.';
        variantControl.focus();
      }
    });

    const restoreFromHistory = () => {
      if (root.isConnected) renderVariant(variantFromUrl() || defaultVariant);
    };
    const onPageShow = (event) => {
      if (event.persisted) restoreFromHistory();
    };
    window.addEventListener('popstate', restoreFromHistory);
    window.addEventListener('pageshow', onPageShow);

    const onSectionUnload = (event) => {
      if (event.detail.sectionId !== root.dataset.sectionId) return;
      window.removeEventListener('popstate', restoreFromHistory);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    };
    document.addEventListener('shopify:section:unload', onSectionUnload);
  };

  const initializeWithin = (scope) => {
    if (scope.matches && scope.matches('[data-tdm-product]')) initializeProduct(scope);
    scope.querySelectorAll('[data-tdm-product]').forEach(initializeProduct);
  };

  // A section may render this asset more than once inside the theme editor.
  if (!window.tdmProductSectionsInitialized) {
    window.tdmProductSectionsInitialized = true;
    document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }
})();
